import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Download, ExternalLink, MessageSquareOff } from 'lucide-react';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { TranscriptView } from '@/components/conversations/TranscriptView';
import { ApiError } from '@/lib/api';
import { formatDateTime } from '@/lib/formatDate';
import { igHandle } from '@/lib/igHandle';
import { Badge } from '@/components/ui/badge';
import { AttentionStatusBadge } from '@/components/shared/AttentionStatusBadge';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { ErrorState, type ErrorStateVariant } from '@/components/shared/ErrorState';
import { PageHeader } from '@/components/shared/PageHeader';
import {
  conversationService,
  type ConversationDetail,
  type ConversationSummary,
} from '@/services/conversationService';

// ---------------------------------------------------------------------------
// Bubbles, day dividers, and the per-turn audit line live in the shared
// `TranscriptView` (F152 extraction) so this page and the `LeadDetailPage`
// conversation card don't fork the bubble implementation.

// Info-grid dates use formatDateTime (dd/mm/yyyy hh:mm); the alias keeps the
// call sites below readable.
const formatDate = formatDateTime;

export function ConversationDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { contactId } = useParams<{ contactId: string }>();
  const contactIdNum = contactId ? Number(contactId) : NaN;

  const [detail, setDetail] = useState<ConversationDetail | null>(null);
  const [error, setError] = useState<ErrorStateVariant | null>(null);
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchDetail = useCallback(async () => {
    if (Number.isNaN(contactIdNum)) {
      setError('not-found');
      return;
    }
    setError(null);
    try {
      const result = await conversationService.get(contactIdNum);
      setDetail(result);
    } catch (e) {
      // F064 #30 — classify into a friendly variant; the server's raw
      // error.message (EN + jargon) is never rendered at the owner.
      setError(e instanceof ApiError && e.status === 404 ? 'not-found' : 'load-failed');
    }
  }, [contactIdNum]);

  useEffect(() => {
    void fetchDetail();
  }, [fetchDetail]);

  const updateSummary = (next: ConversationSummary) => {
    setDetail((prev) => (prev ? { ...prev, summary: next } : prev));
  };

  const handleAssumeControl = async () => {
    if (Number.isNaN(contactIdNum)) return;
    const next = await conversationService.assumeControl(contactIdNum);
    updateSummary(next);
  };

  const handleReleaseControl = async () => {
    if (Number.isNaN(contactIdNum)) return;
    const next = await conversationService.releaseControl(contactIdNum);
    updateSummary(next);
  };

  const handleResolve = async () => {
    if (Number.isNaN(contactIdNum)) return;
    const next = await conversationService.resolve(contactIdNum);
    updateSummary(next);
  };

  const handleEscalateConfirm = async () => {
    if (Number.isNaN(contactIdNum)) return;
    const next = await conversationService.escalate(contactIdNum);
    updateSummary(next);
  };

  const handleExport = async () => {
    if (Number.isNaN(contactIdNum)) return;
    setExporting(true);
    try {
      await conversationService.exportThread(contactIdNum);
    } finally {
      setExporting(false);
    }
  };

  if (detail === null && error === null) {
    return (
      <div className="flex flex-col gap-3 p-6" data-testid="conversation-detail-skeleton">
        <Skeleton className="h-10 w-1/2" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (error && detail === null) {
    return (
      <div className="p-6" data-testid="conversation-detail-error">
        <ErrorState
          variant={error}
          cta={
            error === 'not-found' ? (
              <Button type="button" variant="outline" onClick={() => navigate('/conversations')}>
                {t('conversations.back_to_list')}
              </Button>
            ) : (
              <>
                <Button type="button" variant="outline" onClick={() => void fetchDetail()}>
                  {t('common.retry')}
                </Button>
                <Button type="button" variant="ghost" onClick={() => navigate('/conversations')}>
                  {t('conversations.back_to_list')}
                </Button>
              </>
            )
          }
        />
      </div>
    );
  }

  const summary = detail!.summary;
  const messages = detail!.messages;

  // Friendly Instagram identity: @handle, or a contact-number label — the
  // raw ig_user_id (a technical IGSID) is never shown as the identity.
  const identityLabel =
    igHandle(summary.ig_username) ??
    t('conversations.contact_fallback', { id: summary.contact_id });
  const headline = identityLabel;
  const ownerActive =
    summary.owner_active_until !== null &&
    new Date(summary.owner_active_until).getTime() > Date.now();

  const avatarFallbackChar = summary.ig_username
    ? summary.ig_username.charAt(0).toUpperCase()
    : '#';

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader
        title={headline}
        subtitle={
          <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-2">
            <AttentionStatusBadge status={summary.attention_status} />
            {ownerActive ? (
              <Badge variant="secondary">{t('conversations.owner_active')}</Badge>
            ) : null}
            {summary.known_contact ? (
              <Badge variant="outline">{t('conversations.known')}</Badge>
            ) : null}
            {summary.bot_silenced ? (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <MessageSquareOff className="h-3 w-3" aria-hidden="true" />
                {t('conversations.bot_silenced')}
              </span>
            ) : null}
          </span>
        }
        actions={
          <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-wrap">
            {summary.bot_silenced || ownerActive ? (
              <Button
                type="button"
                variant="outline"
                className="min-h-11"
                data-testid="conversation-release-button"
                onClick={() => void handleReleaseControl()}
              >
                {t('conversations.action.release_control')}
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="min-h-11"
                data-testid="conversation-assume-button"
                onClick={() => void handleAssumeControl()}
              >
                {t('conversations.action.assume_control')}
              </Button>
            )}
            {!summary.resolved_at ? (
              <Button
                type="button"
                variant="outline"
                className="min-h-11"
                data-testid="conversation-resolve-button"
                onClick={() => void handleResolve()}
              >
                {t('conversations.action.resolve')}
              </Button>
            ) : null}
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              data-testid="conversation-escalate-button"
              onClick={() => setEscalateOpen(true)}
            >
              {t('conversations.action.escalate')}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              data-testid="conversation-export-button"
              disabled={exporting}
              onClick={() => void handleExport()}
            >
              <Download className="mr-2 h-4 w-4" aria-hidden="true" />
              {t('conversations.action.export')}
            </Button>
          </div>
        }
      />

      {/* Lead link — show when this conversation is linked to a lead. */}
      {summary.lead_id ? (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">{t('conversations.detail.lead_label')}</span>
          <Link
            to={`/leads/${summary.lead_id}`}
            className="inline-flex items-center gap-1 hover:underline"
            data-testid="conversation-lead-link"
          >
            {t('conversations.detail.view_lead')}
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </Link>
        </div>
      ) : null}

      {/* Info grid */}
      <Card>
        <CardHeader>
          <CardTitle>{t('conversations.detail.info_title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-y-1 text-sm sm:grid-cols-[160px_1fr] sm:gap-y-3 [&>dd]:min-w-0 [&>dd]:break-words [&>dt:not(:first-child)]:mt-2 sm:[&>dt:not(:first-child)]:mt-0">
            <dt className="text-muted-foreground">{t('conversations.detail.ig_user_id')}</dt>
            <dd>{summary.ig_user_id}</dd>
            <dt className="text-muted-foreground">{t('conversations.detail.last_activity')}</dt>
            <dd>{formatDate(summary.last_activity_at)}</dd>
            {summary.resolved_at ? (
              <>
                <dt className="text-muted-foreground">{t('conversations.detail.resolved_at')}</dt>
                <dd>{formatDate(summary.resolved_at)}</dd>
              </>
            ) : null}
          </dl>
        </CardContent>
      </Card>

      {/* Transcript — header echoes the IG-thread identity (avatar + handle),
          structurally mirroring the DM header without touching the
          preview-chat palette (monochrome throughout). */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base font-medium">
            <Avatar className="h-6 w-6 shrink-0">
              <AvatarFallback className="text-xs">{avatarFallbackChar}</AvatarFallback>
            </Avatar>
            <span className="min-w-0 truncate">{identityLabel}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <TranscriptView messages={messages} avatarFallbackChar={avatarFallbackChar} />
        </CardContent>
      </Card>

      <ConfirmDialog
        open={escalateOpen}
        onOpenChange={setEscalateOpen}
        variant="default"
        title={t('conversations.escalate_confirm_title')}
        description={t('conversations.escalate_confirm_body')}
        confirmLabel={t('conversations.action.escalate')}
        onConfirm={handleEscalateConfirm}
      />
    </div>
  );
}
